import { BaseTooltip } from '@/src/components'
import { appColors } from '@/src/theme'
import { Flex, FormControl, FormLabel, type FormLabelProps } from '@chakra-ui/react'

import { DatePicker } from '../DatePicker/DatePicker'
import type { DatePickerType } from '../DatePicker/datePicker.types'

interface IFormDatePickerBase {
  name: string
  label: string
  pickerType?: DatePickerType
  isInlineLabel?: boolean
  isDisabled?: boolean
  minDate?: Date | string
  maxDate?: Date | string
  excludeDates?: Date[]
  filterDate?: (date: Date) => boolean
  disabledYears?: number[]
  infoTooltip?: string
  isRequired?: boolean
  height?: string
  labelProps?: FormLabelProps
  placeholderText?: string
}

interface IFormSingleDatePicker extends IFormDatePickerBase {
  isRange?: false
  value?: string | Date | null
  initialValue?: string | Date | null | undefined
  onChange?: (date: Date | null) => void
}

interface IFormDateRangePicker extends IFormDatePickerBase {
  isRange: true
  value?: [string | Date | null | undefined, string | Date | null | undefined]
  initialValue?: [string | Date | null | undefined, string | Date | null | undefined]
  onChange?: (dateRange: [Date | null, Date | null]) => void
}

type FormDatePickerProps = IFormSingleDatePicker | IFormDateRangePicker

export const FormDatePicker = ({
  name,
  label,
  pickerType = 'day',
  isRange,
  isInlineLabel,
  isDisabled,
  minDate,
  maxDate,
  excludeDates,
  filterDate,
  disabledYears,
  value,
  initialValue,
  infoTooltip,
  isRequired,
  height,
  labelProps = {},
  placeholderText,
  onChange,
}: FormDatePickerProps) => {
  const pickerProps = isRange
    ? ({ isRange: true, value, initialValue, onChange } as const)
    : ({ isRange: false, value, initialValue, onChange } as const)

  return (
    <FormControl isDisabled={isDisabled} isRequired={isRequired}>
      <Flex direction={isInlineLabel ? 'row' : 'column'}>
        {(label || infoTooltip) && (
          <Flex justifyContent='flex-start' marginBottom={2} alignItems={'center'}>
            <FormLabel fontSize='14px' marginBottom={0} color={appColors.secondaryForeground} mr='4px' {...labelProps}>
              {label}
            </FormLabel>
            {infoTooltip && <BaseTooltip blackIcon width={'16px'} height={'16px'} label={infoTooltip} />}
          </Flex>
        )}
        <DatePicker
          name={name}
          height={height}
          pickerType={pickerType}
          isDisabled={isDisabled}
          minDate={minDate}
          maxDate={maxDate}
          excludeDates={excludeDates}
          filterDate={filterDate}
          disabledYears={disabledYears}
          width='100%'
          fontWeight={'400px'}
          placeholderText={placeholderText}
          {...pickerProps}
        />
      </Flex>
    </FormControl>
  )
}
